import { Injectable } from "@angular/core";
import { AccountType } from "../../types/AccountType";
import { AccountOwnerType } from "../../types/AccountOwnerType";
import * as accountDetailsContent from "@services/content/guided-flow/account-details-content";
import * as accountOwnerContent from "@services/content/guided-flow/account-owner-content";
import { DropdownMenuItem } from "@vg-constellation/angular-17/dropdown";

export interface AccountTypeLabels {
  titledUnderLabel: string;
  accountNameLabel: string;
  accountNamePlaceholder: string;
  showSubAccountType: boolean;
}

export interface AccountTypeConfiguration {
  labels: AccountTypeLabels;
  subAccountTypes: DropdownMenuItem[];
  showPod: boolean;
  showMortgageInput: boolean;
  localContent: any;
}

@Injectable({
  providedIn: "root",
})
export class AccountTypeConfigurationService {
  private readonly accountSubTypeMap = {
    [AccountType.RETIREMENT_ANNUITY]: accountDetailsContent.retirementAnnuitySubTypes,
    [AccountType.INVESTMENT]: accountDetailsContent.investmentSubTypes,
    [AccountType.REAL_ESTATE]: accountDetailsContent.realEstateSubTypes,
    [AccountType.TRUST]: accountDetailsContent.trustTypes,
  };

  private readonly accountOwnerTypeMap = {
    [AccountOwnerType.INDIVIDUAL_BY_NAME]: accountOwnerContent.ACCOUNT_OWNER_INDIVIDUAL_BY_NAME,
    [AccountOwnerType.TRUST]: accountOwnerContent.ACCOUNT_OWNER_TRUST,
    // TODO: Uncomment these when we need these back
    // [AccountOwnerType.BUSINESS]: accountOwnerContent.ACCOUNT_OWNER_BUSINESS,
    // [AccountOwnerType.CHARITY]: accountOwnerContent.ACCOUNT_OWNER_CHARITY,
    // [AccountOwnerType.NOT_SURE]: accountOwnerContent.ACCOUNT_OWNER_NOT_SURE
  };

  private readonly accountTypeLabels = {
    [AccountType.MORTGAGE]: {
      titledUnderLabel: accountDetailsContent.ACCOUNT_DETAILS_MORTGAGE_DEBTOR_TITLE,
      accountNameLabel: accountDetailsContent.ACCOUNT_DETAILS_MORTGAGE_DEBTOR_NAME,
      accountNamePlaceholder: accountDetailsContent.ACCOUNT_DETAILS_MORTGAGE_DEBTOR_PLACEHOLDER,
      showSubAccountType: false,
    },
    [AccountType.REAL_ESTATE]: {
      titledUnderLabel: accountDetailsContent.ACCOUNT_DETAILS_TITLED_UNDER,
      accountNameLabel: accountDetailsContent.ASSET_DETAILS_ASSET_NAME,
      accountNamePlaceholder: accountDetailsContent.ASSET_DETAILS_ASSET_NAME_PLACEHOLDER,
      showSubAccountType: true,
    },
    [AccountType.BANK]: {
      titledUnderLabel: accountDetailsContent.ACCOUNT_DETAILS_TITLED_UNDER,
      accountNameLabel: accountDetailsContent.ACCOUNT_DETAILS_ACCOUNT_NAME,
      accountNamePlaceholder: accountDetailsContent.ACCOUNT_DETAILS_ACCOUNT_NAME_PLACEHOLDER,
      showSubAccountType: false,
    },
    [AccountType.LIABILITIES]: {
      titledUnderLabel: accountDetailsContent.ACCOUNT_DETAILS_LIABILITY_DEBTOR_TITLE,
      accountNameLabel: accountDetailsContent.ACCOUNT_DETAILS_LIABILITY_DEBTOR_NAME,
      accountNamePlaceholder: accountDetailsContent.ACCOUNT_DETAILS_LIABILITY_DEBTOR_PLACEHOLDER,
      showSubAccountType: false,
    },
    [AccountType.TRUST]: {
      titledUnderLabel: accountDetailsContent.ACCOUNT_DETAILS_TITLED_UNDER,
      accountNameLabel: accountDetailsContent.ACCOUNT_DETAILS_TRUST_LEGAL_NAME,
      accountNamePlaceholder: "Trust name",
      showSubAccountType: true,
    },
    default: {
      titledUnderLabel: accountDetailsContent.ACCOUNT_DETAILS_TITLED_UNDER,
      accountNameLabel: accountDetailsContent.ACCOUNT_DETAILS_ACCOUNT_NAME,
      accountNamePlaceholder: accountDetailsContent.ACCOUNT_DETAILS_ACCOUNT_NAME_PLACEHOLDER,
      showSubAccountType: true,
    },
  };

  /**
   * Gets the labels configuration for a given account type
   */
  getAccountTypeLabels(accountType: AccountType): AccountTypeLabels {
    return this.accountTypeLabels[accountType] || this.accountTypeLabels.default;
  }

  /**
   * Gets the sub-account types for a given account type
   */
  getSubAccountTypes(accountType: AccountType): DropdownMenuItem[] {
    return this.accountSubTypeMap[accountType] || [];
  }

  /**
   * Gets the account owner type mapping
   */
  getAccountOwnerTypeMap(): { [key in AccountOwnerType]?: string } {
    return this.accountOwnerTypeMap;
  }

  private readonly podEligibleAccountTypes: Set<AccountType> = new Set([
    AccountType.BANK,
    AccountType.RETIREMENT_ANNUITY,
    AccountType.INVESTMENT,
  ]);

  /**
   * Determines if POD (Payable on Death) should be shown.
   * Only eligible for Bank, Retirement, and Investment accounts owned by non-business entities.
   */
  shouldShowPod(ownerType: AccountOwnerType, accountType?: AccountType): boolean {
    if (ownerType === AccountOwnerType.BUSINESS || ownerType === AccountOwnerType.TRUST)
      return false;
    if (accountType && !this.podEligibleAccountTypes.has(accountType)) return false;
    return true;
  }

  /**
   * Determines if sub-account type selection should be shown for the given account type
   */
  shouldShowSubAccountType(accountType: AccountType): boolean {
    const labels = this.getAccountTypeLabels(accountType);
    return labels.showSubAccountType;
  }

  /**
   * Determines if mortgage input should be shown for the given account type
   */
  shouldShowMortgageInput(accountType: AccountType): boolean {
    return accountType === AccountType.REAL_ESTATE;
  }

  /**
   * Gets localized content based on account type and owner type
   */
  getLocalAccountDetailsContent(accountType: AccountType, _ownerType?: string): any {
    if (accountType === AccountType.REAL_ESTATE) {
      return {
        ...accountDetailsContent,
        ACCOUNT_DETAILS_ACCOUNT_NAME: accountDetailsContent.ACCOUNT_DETAILS_PROPERTY_NAME,
        ACCOUNT_DETAILS_ACCOUNT_NAME_PLACEHOLDER:
          accountDetailsContent.ACCOUNT_DETAILS_MORTGAGE_AMOUNT,
        ACCOUNT_DETAILS_BALANCE: accountDetailsContent.ACCOUNT_DETAILS_MARKET_VALUE,
        ACCOUNT_DETAILS_SUBACCOUNT_TYPE: accountDetailsContent.ACCOUNT_DETAILS_PROPERTY_TYPE,
      };
    }

    if (accountType === AccountType.TRUST) {
      return {
        ...accountDetailsContent,
        ACCOUNT_DETAILS_INDIVIDUAL_NAME: accountDetailsContent.ACCOUNT_DETAILS_SETTLOR,
        ACCOUNT_DETAILS_ACCOUNT_NAME: accountDetailsContent.ACCOUNT_DETAILS_TRUST_LEGAL_NAME,
        ACCOUNT_DETAILS_ACCOUNT_NAME_SUB_TEXT:
          accountDetailsContent.ACCOUNT_DETAILS_TRUST_LEGAL_NAME_SUB_TEXT,
        ACCOUNT_DETAILS_BALANCE: accountDetailsContent.TRUST_DETAILS_TRUST_DISPLAY_NAME,
        ACCOUNT_DETAILS_SUBACCOUNT_TYPE: accountDetailsContent.ACCOUNT_DETAILS_TRUST_TYPE,
        ACCOUNT_DETAILS_REAL_ESTATE_REQUIRED_DESC:
          accountDetailsContent.ACCOUNT_DETAILS_TRUST_REQUIRED_DESC,
      };
    }

    return { ...accountDetailsContent };
  }

  /**
   * Gets comprehensive configuration for a given account type and owner type
   */
  getAccountTypeConfiguration(
    accountType: AccountType,
    ownerType?: AccountOwnerType,
  ): AccountTypeConfiguration {
    const labels = this.getAccountTypeLabels(accountType);
    const showPod = ownerType ? this.shouldShowPod(ownerType, accountType) : false;
    const showMortgageInput = this.shouldShowMortgageInput(accountType);

    // Get owner type string for content swapping
    const ownerTypeString = ownerType ? this.accountOwnerTypeMap[ownerType] : undefined;

    const subAccountTypes = this.getSubAccountTypes(accountType);
    const localContent = this.getLocalAccountDetailsContent(accountType, ownerTypeString);

    return {
      labels,
      subAccountTypes,
      showPod,
      showMortgageInput,
      localContent,
    };
  }

  /**
   * Checks if the account type requires sub-account type selection
   */
  requiresSubAccountType(accountType: AccountType): boolean {
    return (
      accountType !== AccountType.BANK &&
      accountType !== AccountType.LIABILITIES &&
      accountType !== AccountType.MORTGAGE
    );
  }

  /**
   * Gets account types that don't have sub-account types
   */
  getAccountTypesWithoutSubTypes(): AccountType[] {
    return [AccountType.BANK, AccountType.LIABILITIES, AccountType.MORTGAGE];
  }
}
